// Componentes reutilizables FASE 1
import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Sparkles, Music, Ticket, User, X, Star } from 'lucide-react';
import { COLORS, paletteFor, fmtDayShort, fmtTime, WEEKDAY_SHORT, MONTHS } from './designSystem';

// Hook: volver atrás con fallback a Home cuando no hay historial (deep link / primera página)
export const useSafeBack = (fallback = '/') => {
  const navigate = useNavigate();
  const location = useLocation();
  return () => {
    if (location.key === 'default' || window.history.length <= 1) navigate(fallback);
    else navigate(-1);
  };
};

// Imagen con fallback de bloque de color + nota musical
// Si la className externa incluye "absolute", respetamos el posicionamiento externo (no forzamos relative).
export const ColorBlockImage = ({ src, venueId, className = '', iconSize = 40, children }) => {
  const [failed, setFailed] = useState(!src);
  const p = paletteFor(venueId);
  const isAbsolute = /\babsolute\b/.test(className);
  const posClass = isAbsolute ? 'overflow-hidden' : 'relative overflow-hidden';
  if (!failed && src) {
    return (
      <div className={`${posClass} ${className}`} style={{ backgroundColor: p.bg }}>
        <img src={src} alt="" loading="lazy" decoding="async" className="w-full h-full object-cover" onError={() => setFailed(true)} />
        {children}
      </div>
    );
  }
  return (
    <div className={`${posClass} flex items-center justify-center ${className}`} style={{ backgroundColor: p.bg }}>
      <Music size={iconSize} color={p.icon} strokeWidth={1.5} />
      {children}
    </div>
  );
};

// Chip pill (activo rojo primario, inactivo oscuro)
export const Chip = ({ active, onClick, children, icon, variant = 'default', ...rest }) => {
  const bg = active ? COLORS.primary : COLORS.surface;
  const border = active ? COLORS.primary : COLORS.borderStrong;
  if (variant === 'ageDark') {
    return (
      <button onClick={onClick} className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full text-sm font-semibold whitespace-nowrap" style={{ backgroundColor: active ? '#4A1620' : COLORS.surface, border: `1px solid ${active ? COLORS.primary : COLORS.borderStrong}`, color: COLORS.text }} {...rest}>
        {icon}{children}
      </button>
    );
  }
  return (
    <button onClick={onClick} className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full text-sm font-semibold whitespace-nowrap transition-colors" style={{ backgroundColor: bg, border: `1px solid ${border}`, color: COLORS.text }} {...rest}>
      {icon}{children}
    </button>
  );
};

// Bottom sheet con backdrop
export const BottomSheet = ({ open, onClose, children, title, testid }) => {
  useEffect(() => {
    if (!open) return;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = ''; };
  }, [open]);
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center" data-testid={testid}>
      <div className="absolute inset-0 bg-black/70" onClick={onClose} />
      <div className="relative w-full max-w-[430px] mx-auto rounded-t-[28px] p-5 pb-8 max-h-[90vh] overflow-y-auto" style={{ backgroundColor: COLORS.surfaceSheet }}>
        <div className="w-10 h-1 rounded-full bg-white/20 mx-auto mb-4" />
        {(title || onClose) && (
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-white text-lg font-bold">{title}</h3>
            {onClose && <button onClick={onClose} className="w-8 h-8 rounded-full flex items-center justify-center" style={{ backgroundColor: COLORS.surface }} aria-label="Cerrar"><X size={16} color={COLORS.text} /></button>}
          </div>
        )}
        {children}
      </div>
    </div>
  );
};

// Bottom nav 4 pestañas
export const BottomNav = () => {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const items = [
    { label: 'Eventos', path: '/', match: ['/', '/eventos'], icon: Sparkles },
    { label: 'Venues', path: '/venues', match: ['/venues'], icon: Music },
    { label: 'Entradas', path: '/entradas', match: ['/entradas'], icon: Ticket },
    { label: 'Perfil', path: '/perfil', match: ['/perfil'], icon: User },
  ];
  // Hide on event detail
  if (/^\/eventos\/[^/]+(\/comprar)?$/.test(pathname)) return null;
  return (
    <nav className="fixed bottom-0 left-1/2 -translate-x-1/2 w-full max-w-[430px] z-40 pb-safe" style={{ backgroundColor: COLORS.surfaceNav, borderTop: `1px solid ${COLORS.border}` }} data-testid="bottom-nav">
      <div className="flex items-center justify-around py-2.5">
        {items.map((it) => {
          const Icon = it.icon;
          const active = it.match.some((m) => (m === '/' ? pathname === '/' : pathname.startsWith(m)));
          const color = active ? COLORS.coral : COLORS.textMuted;
          return (
            <button key={it.label} onClick={() => navigate(it.path)} className="flex flex-col items-center justify-center flex-1 gap-0.5" style={{ color }} data-testid={`nav-${it.label.toLowerCase()}`}>
              <Icon size={22} />
              <span className="text-[11px] font-semibold">{it.label}</span>
            </button>
          );
        })}
      </div>
    </nav>
  );
};

// Card de evento recomendado (carrusel home) — flyer full-bleed + overlay con texto
export const EventCardLarge = ({ event, onClick }) => (
  <button onClick={onClick} className="shrink-0 w-[280px] text-left" data-testid={`event-card-${event.id}`}>
    <div className="relative rounded-[16px] overflow-hidden aspect-[3/4]">
      <ColorBlockImage src={event.image || event.imagen_url} venueId={event.venue_id} className="absolute inset-0" iconSize={56} />
      {/* Gradiente oscuro inferior para legibilidad del texto superpuesto */}
      <div className="absolute inset-x-0 bottom-0 h-[62%] pointer-events-none" style={{ background: 'linear-gradient(to top, rgba(0,0,0,0.92) 0%, rgba(0,0,0,0.75) 30%, rgba(0,0,0,0.35) 65%, rgba(0,0,0,0) 100%)' }} />
      {event.fecha_inicio && (
        <div className="absolute top-3 right-3 px-2.5 py-1 rounded-full text-[11px] font-semibold text-white" style={{ backgroundColor: 'rgba(10,10,12,0.65)', backdropFilter: 'blur(6px)', WebkitBackdropFilter: 'blur(6px)' }}>
          {fmtDayShort(event.fecha_inicio)}
        </div>
      )}
      {event.ultimas_entradas && (
        <div className="absolute top-3 left-3 px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-wide" style={{ backgroundColor: COLORS.primaryDark, color: COLORS.coral }}>
          Últimas
        </div>
      )}
      {/* Texto superpuesto en la base */}
      <div className="absolute inset-x-0 bottom-0 p-3.5">
        <h3 className="text-white font-extrabold text-[17px] leading-tight mb-0.5 line-clamp-2" style={{ textShadow: '0 1px 8px rgba(0,0,0,0.6)' }}>{event.name}</h3>
        <p className="text-[13px] font-medium mb-2 line-clamp-1" style={{ color: 'rgba(255,255,255,0.82)', textShadow: '0 1px 6px rgba(0,0,0,0.6)' }}>{event.venue} · Madrid</p>
        <div className="flex items-end justify-between">
          <span className="text-white font-extrabold text-xl" style={{ textShadow: '0 1px 6px rgba(0,0,0,0.5)' }}>{event.price > 0 ? `${Number(event.price).toFixed(0)}€` : 'Gratis'}</span>
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold" style={{ backgroundColor: 'rgba(10,10,12,0.55)', border: `1px solid ${COLORS.primaryDark}`, color: COLORS.coral, backdropFilter: 'blur(4px)', WebkitBackdropFilter: 'blur(4px)' }}>
            <Star size={10} fill={COLORS.coral} color={COLORS.coral} /> +{event.puntos || 0}
          </span>
        </div>
      </div>
    </div>
  </button>
);

// Row de evento (lista)
export const EventRow = ({ event, onClick }) => {
  const time = event.fecha_inicio ? fmtTime(event.fecha_inicio) : (event.day || '');
  return (
    <button onClick={onClick} className="w-full text-left flex items-center gap-3 p-2.5 rounded-[18px]" style={{ backgroundColor: COLORS.surface, border: `1px solid ${COLORS.border}` }} data-testid={`event-row-${event.id}`}>
      <ColorBlockImage src={event.image || event.imagen_url} venueId={event.venue_id} className="w-[60px] h-[60px] shrink-0 rounded-xl" iconSize={24} />
      <div className="flex-1 min-w-0">
        <p className="text-sm font-semibold mb-0.5" style={{ color: COLORS.coral }}>{time} · {event.venue}</p>
        <h4 className="text-white font-bold text-[15px] leading-tight line-clamp-1">{event.name}</h4>
        <div className="flex items-center gap-1.5 mt-1.5 flex-wrap">
          <span className="px-2 py-0.5 rounded-full text-[10px] font-semibold" style={{ backgroundColor: COLORS.surfaceAlt, color: COLORS.text }}>+{event.edad_minima || 18}</span>
          {(event.estilos || []).slice(0, 2).map((s) => (
            <span key={s} className="px-2 py-0.5 rounded-full text-[10px] font-semibold" style={{ backgroundColor: COLORS.surfaceAlt, color: COLORS.text }}>{s}</span>
          ))}
          {event.ultimas_entradas && <span className="px-2 py-0.5 rounded-full text-[10px] font-bold" style={{ backgroundColor: COLORS.primaryDark, color: COLORS.coral }}>Últimas</span>}
        </div>
      </div>
      <div className="shrink-0 self-end pb-1">
        <span className="text-white font-extrabold text-base whitespace-nowrap">{event.price > 0 ? `${Number(event.price).toFixed(0)}€` : 'Gratis'}</span>
      </div>
    </button>
  );
};

// Tile de venue
export const VenueTile = ({ venue, onClick, size = 'small' }) => {
  const dim = size === 'large' ? 'aspect-square w-full' : 'w-[150px] aspect-[4/5]';
  return (
    <button onClick={onClick} className="shrink-0 text-left" style={{ width: size === 'large' ? '100%' : 150 }} data-testid={`venue-tile-${venue.id}`}>
      <ColorBlockImage src={venue.image || venue.imagen_url} venueId={venue.id} className={`${dim} rounded-[18px] mb-2 overflow-hidden`} iconSize={42} />
      <h3 className="text-white font-bold text-[14px] leading-tight mb-0.5 line-clamp-1">{venue.name}</h3>
      <p className="text-[12px]" style={{ color: COLORS.textMuted }}>{venue.eventos_proximos || 0} eventos próximos</p>
    </button>
  );
};

// Phone frame wrapper
export const PhoneShell = ({ children }) => (
  <div className="min-h-screen" style={{ backgroundColor: '#000' }}>
    <div className="mx-auto max-w-[430px] min-h-screen relative overflow-x-hidden pb-24" style={{ backgroundColor: COLORS.bg }}>
      {children}
    </div>
  </div>
);
